<script setup lang="ts" generic="T extends Record<string, any>">
import { defu } from 'defu'

export type DataColumn<D> = {
  title?: string
  key: keyof D
  minWidth?: number
  isHeading?: boolean
  render?: (arg: D) => string | undefined | null
  renderComponent?: (arg: D) => VNode | undefined | null
  isVisible?: (arg: D) => boolean
  rowProps?: Record<string, any>
  hideIfNullish?: boolean
}

export type DataListLayout = 'list' | 'grid' | 'none'
export type DataListSize = 'sm' | 'base' | 'lg'
export type DataListStyling = {
  layout?: DataListLayout
  size?: DataListSize
  wrapperClass?: string
}

const props = defineProps<{
  config: { data?: T | null, columns: DataColumn<T>[], hiddenKeys?: Array<keyof T> }
  styling?: DataListStyling
}>()

const defaultStyles: DataListStyling = { layout: 'list', size: 'base' }
const { value: { layout, size, wrapperClass } } = computed<DataListStyling>(() => defu(props.styling, defaultStyles))

const filteredColumns = computed(() => props.config.columns.filter((column) => {
  if (!props.config.data) {
    return true
  }

  if (props.config.hiddenKeys && props.config.hiddenKeys.includes(column.key)) {
    return false
  }

  if (column.hideIfNullish) {
    const value = column.render ? column.render(props.config.data) : props.config.data[column.key]
    return typeof value !== 'undefined' && value !== null && value !== ''
  }

  if (column.isVisible) {
    return column.isVisible(props.config.data)
  }

  return true
}))
</script>

<template>
  <div>
    <ul
      class="overflow-auto"
      :class="[
        layout === 'list' ? 'flex flex-col gap-2' : '',
        layout === 'grid' ? 'grid gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4' : '',
        size === 'sm' ? 'text-sm' : '',
        size === 'base' ? 'text-base' : '',
        size === 'lg' ? 'text-lg' : '',
        wrapperClass,
      ]"
    >
      <li v-for="column in filteredColumns" :key="column.key" v-bind="column.rowProps">
        <h4 v-if="column.title" class="font-semibold">
          {{ column.title }}:
        </h4>
        <div v-if="!column.isHeading">
          <div v-if="!config.data">
            {{ $t('app.blank') }}
          </div>
          <div v-else-if="column.render">
            {{ column.render(config.data) || $t('app.blank') }}
          </div>
          <div v-else-if="column.renderComponent" class="mt-1">
            <component :is="column.renderComponent(config.data)" />
          </div>
          <span v-else>
            {{ config.data[column.key] || $t('app.blank') }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>
